<template>
    <div v-if="ready">
        <form
            class="form-horizontal fr-absolute"
            enctype="multipart/form-data"
            @submit.prevent="store"
            @keydown="form.errors.clear($event.target.name)"
            novalidate
            autocomplete="off"
        >
            <form-textarea
                :form="form"
                label="subject_or_short_description"
                name="subject"
                :required="true"
            />

            <form-textarea
                :form="form"
                label="body_or_explanation"
                name="body"
                :required="true"
            />

            <yes-no-select
                :form="form"
                v-model="form.reporting_log"
                name="reporting_log"
                label="reporting_log_include"
                :required="true"
            />


            <form-date-picker
                :form="form"
                name="action_date"
                label="action_date"
                initial-view="day"
                required
            />

            <div class="form-group fr-absolute">
                <input
                    :disabled="form.busy"
                    type="submit"
                    class="btn btn-brand"
                    @click.prevent="store()"
                    :value="uLang('app.save')"
                />
            </div>
        </form>
    </div>
</template>

<script>
import {
    Input as FormInput,
    Textarea as FormTextarea,
} from '~/Form/elements'
import Form from '~/Form/Form'
import FormMixin from '~/mixins/form'
import moment from 'moment'
import {WarningAlert} from "~/Swals"
import YesNoSelect from "../../elements/selects/yes-no";
import Swal from 'sweetalert'

export default {
    name: 'LogForm',

    mixins: [FormMixin],

    components: {
        YesNoSelect,
        FormInput,
        FormTextarea,
    },

    props: {
        id: {
            required: false,
        },
        entityType: {
            type: String,
            required: true,
        },
        entityId: {
            required: true,
        },
        trajectoryId: {
            required: true,
        },
        milestoneProgressId: {
            required: true,
        },
        actionProgressId: {
            required: true,
        }
    },

    data() {
        return {
            entity: 'log',
            form: new Form({
                entity_type: '',
                entity_id: null,
                reporting_log_type: null,
                reporting_log_activity_type: null,
                log_category_id: null,
                activity_date: new Date(),
                action_date: new Date(),
                reminder: '',
                action_progress_ids: [], 
                subject: '',
                relations: [],
                body: '',
                departments: [],
                reporting_log: false,
                who_can_see: {
                    backoffice: true,
                    trajectory_manager: true,
                    candidate: !!$hasType('candidate') ?? false,
                    employer: !!$hasType('client') ?? false
                },
                form_coach_page: true,
                this_coach_action_progress_id: 0,
            }),
            baseUri: '',
            selected_category: null,
            selected_reporting_log_type: {
                "value": "(ondernomen) Activiteit",
                "system_name": "reporting_log_activity",
            },
            add_to_report: false,
                resetForm: false,
                departments: [],
            };
        },

        watch: {
            entityType: {
                handler: 'setFormEntityType',
                immediate: true,
            },
            entityId: {
                handler: 'setFormEntityId',
                immediate: true,
            },
            selected_category(newValue) {
                let parsedValue = newValue == null ? null : newValue.id;
                this.form.log_category_id = parsedValue;
                this.$emit('categoryChanged', parsedValue)
            },
            selected_reporting_log_type(newValue) {
                this.form.reporting_log_type = $getIfExists(newValue, 'id', null);
            }
        },

        computed: {
            // ID-266: not needed anymore,
            // entityRelations() {
            //     return route('logs.relations', {entity: this.entityType, entity_id: this.entityId})
            // },

            entityDepartments() {
                return route('logs.departments', {entity: this.entityType, entity_id: this.entityId})
            },
            isLogForRelation() {
                return this.entityType === 'relations';
            },
            /**
             * Date should be shown when its an "Activiteit" or a "Sollicitatieactiviteit"
             */
            shouldShowDateField() {
                return this.currentReportingLogTypeIs('reporting_log_activity')
                    || this.currentReportingLogTypeIs('reporting_log_follow_up_advice')
                    || true
            },
            disableBackoffice() {
                return false
            },
            disableTrajectoryManager() {
                return !!$hasType('coach')
            },
            disableCandidate() {
                return !!$hasType('candidate')
            },
            disableClient() {
                return !!$hasType('client')
            },
            isReportingLog() {
                return !!this.form.reporting_log
            }
        },

        created: function () {
            this.departments = window.config.departments;
            this.$on('afterStore', this.afterStore);
            this.$on('afterUpdate', this.afterUpdate);
            this.$on('onError', this.onError);
            this.$on('afterRetrieve', this.afterRetrieve);
        },

        methods: {
            currentLogCategoryIs(category) {
                return category === $getIfExists(this.selected_category, 'system_name');
            },

            currentReportingLogTypeIs(type) {
                return type === $getIfExists(this.selected_reporting_log_type, 'system_name');
            },

            afterStore(response) {
                // Just clean out the subject and body fields
                this.form.fill({
                    'subject': '',
                    'body': '',
                    'reporting_log': 0,
                    'relations': [],
                    'departments': []
                });

                this.$emit('logCreated', response)
            },

            showInfoModal() {
                Swal({
                    title: uLang('app.information_permission_system'),
                    text: uLang('app.information_permission_system_text_part_1') + '\n' + uLang('app.information_permission_system_text_part_2'),
                    icon: 'info'
                })
            },

            afterUpdate(response) {
                this.$router.push({
                    name: 'admin.relations.trajectory-enrolments.milestone-progresses.action-progresses.action-document.update',
                    params: {
                        relationId: this.entityId,
                        trajectoryEnrolmentId: this.trajectoryId,
                        milestoneProgressId: this.milestoneProgressId,
                        actionProgressId: this.actionProgressId
                    }});
            },

            onError() {
                new WarningAlert({'title': uLang('app.validation_error'), 'text': uLang('app.fill_all_fields')}).show()
            },

            afterRetrieve(log) {
                this.form.log_category_id = log.log_category_id;
                this.selected_category = log.category;
                this.form.relations = log.relations.map((relation) => relation.id);
                this.form.reporting_log_type = log.reporting_log_type_id;
                this.form.activity_date = moment(log.activity_date, 'DD-MM-YYYY')._d; // hackerman the right date
                this.form.action_date = moment(log.action_date, 'YYYY-MM-DD')._d;
                this.form.reminder = !log.reminder ? '' : moment(log.reminder, 'YYYY-MM-DD')._d;
                this.form.reporting_log_activity_type = log.log_subcategory_id;
                this.selected_reporting_log_type = log.reporting_log_type_id;
                this.form.who_can_see = JSON.parse(log.who_can_see);
                this.form.reporting_log = log.reports.length > 0 ? 1 : 0;
                this.form.this_coach_action_progress_id = this.actionProgressId;

                let actionProgresses = log.reports.map(actionprogress => actionprogress.id);
                this.form.action_progress_ids = actionProgresses;
                this.add_to_report = actionProgresses.length > 0;
            },

            setFormEntityType(newValue) {
                this.form.entity_type = newValue
            },

            setFormEntityId(newValue) {
                this.form.entity_id = newValue
            },

            /**
             * Remove any coupled action progresses when the 'add to report' switch is turned off on submit
             * Also clear out the activity type, in case the log type isn't 'activity' anymore
             */
            beforeStore() {
                if (!this.form.reporting_log) {
                    this.form.action_progress_ids = [];
                }
                if (!this.shouldShowDateField) {
                    this.form.reporting_log_activity_type = null;
                    this.form.activity_date = null;
                }
            },
        },
    };
</script>

<style lang="scss" scoped>
    .info-message {
        display: flex;
        align-items: center;

        i {
            padding-right: 15px;
        }

        p {
            flex: 1;
            margin-bottom: 0;
        }
    }
</style>
